/**
 * SoleadLab 2.0 — Manual indication creation core
 *
 * Shared by the IndicationPopup save path (EditorPage.handleAddIndicationSave)
 * and the quick-add path (MapCanvas armed click with a pre-selected type), so
 * the two flows can never drift on defaults or the properties mapping.
 *
 * Deliberately NOT here: modal state, Mapbox Draw selection, notifications —
 * those stay caller-specific (the popup path selects the new feature for
 * immediate editing; the quick-add path must not, or drag handles would sit
 * under the user's next rapid click).
 */

import { useProjectStore, type IndicationType, type Indication } from '../stores/projectStore';
import { useUIStore } from '../stores/uiStore';
import { useShareSessionStore } from '../stores/shareSessionStore';
import { useAuthStore } from '../stores/authStore';
import { isShareViewerState, isFieldLinkState } from '../hooks/useShareViewer';
import { defaultSizeForType, rectGeomFromCenter, rotatePolygonAroundCenter } from './geometry';
import { resolveCurrentOrtho } from './activeOrtho';
import { planTopologyUpdate } from '../domain/topology/applyDerivedTopology';
import { cellFitAtPoint, stringFitAtPoint } from '../domain/topology/moduleGrid';
import type { SiteLayout } from '../domain/layout/types';
import type { IndicationFormData } from '../components/editor/IndicationPopup';

/** Container types keep their fixed default size — they are not faults and
 *  never take the cell's shape (Photo is a container; DriveImportModal and
 *  MediaTicket build it; it is excluded from CSV for the same reason). */
const NON_FAULT_TYPES = new Set(['Photo', 'Video', 'DataPoint']);

/**
 * T-14: the geometry a manually-placed indication of `type` gets at
 * `coords`. Fault marks on a gridded site take the SIZE AND BEARING of the
 * cell under the click (per-string overrides respected). A String OC takes
 * the FULL footprint of the string under the click — the grid's own module
 * count, rows and bearing for THAT string, not the site-wide default — so
 * the marker matches the drawn string without per-marker editing (owner
 * request 2026-09-14). SHAPE only, POSITION is the click: ortho and grid
 * are never perfectly aligned, the marker fits the ortho and the grid only
 * suggests the string/module. Topology always comes from the area derive on
 * the built rect afterwards.
 * Fallbacks (no grid / off-grid click / container types): the uiStore
 * defaults, unchanged.
 */
export function plannedIndicationGeometry(
  coords: [number, number],
  type: IndicationType,
  opts: {
    layouts: SiteLayout[];
    defaultStringModules: number;
    defaultIndicationOrientation: number;
  },
): { geometry: GeoJSON.Polygon; rotationDeg: number } {
  const moduleCount = type === 'String OC' ? opts.defaultStringModules : undefined;
  const stringFit = type === 'String OC'
    ? stringFitAtPoint(opts.layouts, coords[0], coords[1])
    : null;
  const fit = !stringFit && !NON_FAULT_TYPES.has(type)
    ? cellFitAtPoint(opts.layouts, coords[0], coords[1])
    : null;

  let w: number, h: number, rotationDeg: number;
  if (stringFit) {
    w = stringFit.w;
    h = stringFit.h;
    rotationDeg = stringFit.directionDeg;
  } else if (fit) {
    w = fit.w;
    h = fit.h;
    rotationDeg = fit.directionDeg;
  } else {
    const sz = defaultSizeForType(type, moduleCount);
    w = sz.w;
    h = sz.h;
    rotationDeg = opts.defaultIndicationOrientation || 0;
  }
  const base = rectGeomFromCenter(coords[0], coords[1], w, h);
  return {
    geometry: rotationDeg ? rotatePolygonAroundCenter(base, coords, rotationDeg) : base,
    rotationDeg,
  };
}

/**
 * Geometry for an EXISTING marker re-typed to String OC (Photo → String OC
 * in the preview / flight-path popups): the string footprint under the
 * marker's centre, centre untouched. Off-grid it falls back to the default
 * rect at the marker's current rotation — exactly what the popups did before.
 */
export function retypedStringOcGeometry(
  center: [number, number],
  currentRotationDeg: number,
): { geometry: GeoJSON.Polygon; rotationDeg: number } {
  const d = useProjectStore.getState().data;
  const layouts = d.siteLayouts ?? (d.siteLayout ? [d.siteLayout] : []);
  const { defaultStringModules } = useUIStore.getState();
  return plannedIndicationGeometry(center, 'String OC', {
    layouts,
    defaultStringModules,
    defaultIndicationOrientation: currentRotationDeg,
  });
}

/**
 * BUILD (no store write) a manually-placed indication at `coords`, applying
 * the uiStore defaults (orientation, observation-time preset, String OC
 * module count) and the captureDate fallback chain. Returns null in
 * share-viewer mode (defense in depth — never build phantom indications for
 * visitors).
 *
 * Split out of createManualIndication so batch flows (detection bulk-accept)
 * can build N indications through the SAME core and persist them with one
 * bulk write instead of N per-item creates.
 */
export function buildManualIndication(
  coords: [number, number],
  form: IndicationFormData,
): Indication | null {
  // FL-2: FIELD-kind share visitors may build (quick-add from the field);
  // view-kind visitors never do (defense in depth, unchanged).
  const shareActive = useShareSessionStore.getState().active;
  const authToken = useAuthStore.getState().token;
  const shareView = useShareSessionStore.getState().view;
  if (
    isShareViewerState(shareActive, authToken) &&
    !isFieldLinkState(shareActive, authToken, shareView)
  ) {
    return null;
  }

  const { defaultObservationTime, defaultStringModules, defaultIndicationOrientation } =
    useUIStore.getState();
  const projectState = useProjectStore.getState();

  const now = new Date().toISOString();
  // Observation-time preset (Indications panel) stamps the real observed time
  // on manually-placed indications, and its date part as the capture day so
  // day + time stay consistent. Empty ⇒ current time.
  const observationPreset = defaultObservationTime;
  const createdAtIso = observationPreset ? new Date(observationPreset).toISOString() : now;

  // T-14: shape from the cell under the click (size + bearing; per-string
  // overrides respected); uiStore defaults when no cell answers.
  const layouts = projectState.data.siteLayouts
    ?? (projectState.data.siteLayout ? [projectState.data.siteLayout] : []);
  const { geometry: finalGeom, rotationDeg } = plannedIndicationGeometry(coords, form.type as IndicationType, {
    layouts,
    defaultStringModules,
    defaultIndicationOrientation,
  });
  const indicationId = `ind_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Fallback chain mirrors the popup's initial form state — only the quick-add
  // path arrives without a captureDate.
  const captureDate =
    form.captureDate ||
    projectState.selectedDate ||
    projectState.availableDates[0] ||
    new Date().toISOString().split('T')[0];

  return {
    id: indicationId,
    type: form.type as any,
    geometry: finalGeom,
    properties: {
      title: form.type,
      description: form.notes,
      captureDate: observationPreset ? observationPreset.slice(0, 10) : captureDate,
      station: form.station,
      inverter: form.inverter,
      mppt: form.mppt,
      dccb: form.dccb,
      string: form.string,
      module: form.module,
      linkLibrary: form.linkLibrary,
      urlOverride: form.urlOverride,
      srtUrl: form.srtUrl,
      createIssue: form.createIssue,
      thermalData: form.thermalData,
      image: form.image,
      customFields: form.customFields,
      customTypeName: form.type === 'Custom' ? (form.customTypeName?.trim() || undefined) : undefined,
      rotationDeg,
      // Fault-detection provenance (P2a): which ortho layer the mark was
      // placed against — exemplar scoping for "Find similar" scans.
      // undefined (dropped on serialize) when no ortho layer is active.
      orthoLayerId: resolveCurrentOrtho()?.id,
      createdAt: createdAtIso,
      updatedAt: now,
    },
  };
}

/**
 * Build + add a manually-placed indication at `coords` (see
 * buildManualIndication for the defaults applied). Registers the capture day
 * with the date filter. Returns the new indication id, or null in
 * share-viewer mode (defense in depth — the Add controls are already hidden
 * for visitors, but never create phantom local-only indications).
 *
 * No `status`/`statusEvents` are stamped — absent status ⇒ 'open'.
 */
export function createManualIndication(
  coords: [number, number],
  form: IndicationFormData,
): string | null {
  const indication = buildManualIndication(coords, form);
  if (!indication) return null;

  const projectState = useProjectStore.getState();
  projectState.addIndication(indication);

  // Register the (possibly new) capture day so the date filter lists it and
  // makes it visible — otherwise a manual indication with a fresh date (e.g.
  // from the observation-time preset) is hidden by the date filter.
  projectState.updateAvailableDates();

  return indication.id;
}

/**
 * Quick-add form for a pre-selected `type` with everything else defaulted.
 * Topology fields are EMPTY here — since the deriveTopology routing
 * (2026-09-02), quick-add derives on the indication's FINAL geometry via
 * deriveTopologyIntoIndication, not on the raw click point (a String OC
 * rect spans ~28m; the point saw one cell). The popup path still never
 * re-applies a suggestion the user dismissed or edited away.
 */
export function quickIndicationForm(type: IndicationType): IndicationFormData {
  return {
    type,
    station: '',
    inverter: '',
    mppt: '',
    dccb: '',
    string: '',
    module: '',
    linkLibrary: '',
    urlOverride: '',
    srtUrl: '',
    notes: '',
    createIssue: false,
    customFields: undefined,
  };
}

/**
 * Derive topology for a just-built indication from its REAL geometry, over
 * ALL layouts (T-09), and merge the fills (+ provenance). A new indication
 * has no stored values, so no conflicts are possible; an off-grid marker on
 * a gridded site derives NOTHING (owner ruling 2026-09-02).
 */
export function deriveTopologyIntoIndication(indication: Indication): Indication {
  const d = useProjectStore.getState().data;
  const layouts = d.siteLayouts ?? (d.siteLayout ? [d.siteLayout] : []);
  if (layouts.length === 0) return indication;
  const plan = planTopologyUpdate(indication, layouts);
  if (!plan?.updates) return indication;
  return {
    ...indication,
    properties: { ...indication.properties, ...plan.updates } as Indication['properties'],
  };
}

/**
 * Quick-add path: create an indication of a pre-selected `type` at `coords`
 * (defaults + geometry-aware topology derive at creation).
 */
export function quickCreateIndication(
  coords: [number, number],
  type: IndicationType,
): string | null {
  const indication = buildQuickIndication(coords, type);
  if (!indication) return null;
  const projectState = useProjectStore.getState();
  projectState.addIndication(indication);
  projectState.updateAvailableDates(); // see createManualIndication
  return indication.id;
}

/**
 * BUILD (no store write) a quick-add indication — the batch-accept flavor of
 * quickCreateIndication. Same creation core, same geometry-aware derive.
 * Callers that REPLACE the geometry afterwards (detection accepts) must use
 * detectionAccept.buildAcceptedIndication instead, which derives AFTER the
 * real ring is set — deriving here and swapping geometry later stamps fields
 * from a location the mark no longer occupies.
 */
export function buildQuickIndication(
  coords: [number, number],
  type: IndicationType,
): Indication | null {
  const base = buildManualIndication(coords, quickIndicationForm(type));
  return base ? deriveTopologyIntoIndication(base) : null;
}
